import axios from '../../helpers/axiosRequests';
import moment from 'moment';

const INITIAL_STATE = {
  salesVsMonths: {
    chart: {
      labels: [],
      datasets: [
        {
          label: 'Sales montly',
          backgroundColor: '#f87979',
          pointBackgroundColor: 'white',
          borderWidth: 1,
          pointBorderColor: '#249EBF',
          data: [],
        },
      ],
    },
    loading: false,
    error: null,
  },
  salesVsCompanies: {
    chart: {
      labels: [],
      datasets: [
        {
          label: 'sales share',
          backgroundColor: [
            '#41B883',
            '#E46651',
            '#00D8FF',
            '#f362c2',
            '#4998df',
            '#4443cc',
          ],
          data: [],
        },
      ],
    },
    loading: false,
    error: null,
  },

  other: {
    loading: false,
    totalSales: null,
    weeklySales: null,
    totalCompanies: null,
    totalAgents: null,
    totalDrafts: null,
    error: null,
  },
};

const actions = {
  fetchSalesVsMonths: async (
    { commit },
    { start = moment().startOf('year'), end = moment(), companyId = null } = {}
  ) => {
    commit('SET_LOADING_SALES_VS_MONTHS');
    try {
      const data = await axios.get(
        `/statistics/sales_vs_months${
          start && end ? `?start=${start}&end=${end}` : ''
        }${companyId ? `&companyId=${companyId}` : ''}`
      );
      commit('SET_SALES_VS_MONTHS_DATA', data);
    } catch (error) {
      commit('SET_SALES_VS_MONTHS_ERROR', error);
    }
  },
  fetchSalesVsCompanies: async (
    { commit, rootState },
    { start = moment().startOf('year'), end = moment(), companyId = null } = {}
  ) => {
    try {
      const { currentUser } = rootState.auth;
      let url = `/statistics/sales_vs_companies?${
        start && end ? `start=${start}&end=${end}` : ''
      }`;
      if (currentUser.role === 'supervisor' || companyId) {
        url = `/statistics/sales_vs_agents?${
          start && end ? `start=${start}&end=${end}` : ''
        }${companyId ? `&companyId=${companyId}` : ''}`;
      }
      const data = await axios.get(url);
      const labels = data.map((company) => company.name);
      const chartData = data.map((company) => company.sales.length);
      commit('SET_SALES_VS_COMPANIES_DATA', { labels, data: chartData });
    } catch (error) {
      commit('SET_SALES_VS_COMPANIES_ERROR', error);
    }
  },
  fetchOtherStats: async (
    { commit, rootState },
    { start = moment().startOf('year'), end = moment(), companyId = null } = {}
  ) => {
    try {
      const { currentUser } = rootState.auth;
      commit('SET_OTHER_STATS_LOADING');
      let totalCompanies;
      let totalDrafts;
      const totalSales = await axios.get(
        `/statistics/total_sales?${
          start && end ? `start=${start}&end=${end}` : ''
        }${companyId ? `&companyId=${companyId}` : ''}`
      );
      if (['admin', 'superAdmin'].includes(currentUser.role))
        totalCompanies = await axios.get('/statistics/total_companies');
      if (currentUser.role === 'supervisor' || companyId)
        totalDrafts = await axios.get(
          `/statistics/total_drafts?${
            companyId ? `&companyId=${companyId}` : ''
          }`
        );
      const totalAgents = await axios.get(
        `/statistics/total_agents?${companyId ? `&companyId=${companyId}` : ''}`
      );
      const weeklySales = await axios.get(
        `/statistics/week_sales?${companyId ? `&companyId=${companyId}` : ''}`
      );
      const results = await Promise.allSettled([
        totalSales,
        totalCompanies,
        weeklySales,
        totalAgents,
        totalDrafts,
      ]);

      const data = {};
      results.forEach((result, index) => {
        switch (result.status) {
          case 'fulfilled':
            if (index === 0 && result.value) data.totalSales = result.value;
            if (index === 1 && result.value) data.totalCompanies = result.value;
            if (index === 2 && result.value) data.weeklySales = result.value;
            if (index === 3 && result.value) data.totalAgents = result.value;
            if (index === 4 && result.value) data.totalDrafts = result.value;

            break;

          default:
            break;
        }
      });

      commit('SET_OTHER_STATS_DATA', data);
    } catch (error) {
      commit('SET_OTHER_STATS_ERROR', error);
    }
  },
};

const mutations = {
  SET_LOADING_SALES_VS_MONTHS(state) {
    state.salesVsMonths = {
      ...state.salesVsMonths,
      loading: true,
    };
  },
  SET_SALES_VS_MONTHS_DATA(state, data) {
    state.salesVsMonths = {
      ...state.salesVsMonths,
      loading: false,
      chart: {
        ...state.salesVsMonths.chart,
        labels: Object.keys(data),
        datasets: [
          {
            ...state.salesVsMonths.chart.datasets[0],
            data: Object.values(data).map((items) => items.length),
          },
        ],
      },
    };
  },
  SET_SALES_VS_MONTHS_ERROR(state, error) {
    state.salesVsMonths = {
      ...state.salesVsMonths,
      loading: false,
      error,
    };
  },
  SET_LOADING_SALES_VS_COMPANIES(state) {
    state.salesVsCompanies = {
      ...state.salesVsCompanies,
      loading: true,
    };
  },
  SET_SALES_VS_COMPANIES_DATA(state, { labels, data }) {
    state.salesVsCompanies = {
      ...state.salesVsCompanies,
      loading: false,
      chart: {
        ...state.salesVsCompanies.chart,
        labels,
        datasets: [
          {
            ...state.salesVsCompanies.chart.datasets[0],
            data,
          },
        ],
      },
    };
  },
  SET_SALES_VS_COMPANIES_ERROR(state, error) {
    state.salesVsCompanies = {
      ...state.salesVsCompanies,
      loading: false,
      error,
    };
  },

  SET_OTHER_STATS_LOADING(state) {
    state.other = {
      ...state.other,
      loading: true,
    };
  },
  SET_OTHER_STATS_DATA(state, data) {
    state.other = {
      ...state.other,
      loading: false,
      ...data,
    };
  },
  SET_OTHER_STATS_ERROR(state, error) {
    stats.other = {
      ...state.other,
      loading: false,
      error,
    };
  },
};

const getters = {
  salesVsMonths: (state) => state.salesVsMonths,
  salesVsCompanies: (state) => state.salesVsCompanies,
  otherStats: (state) => state.other,
};

export const stats = {
  namespaced: true,
  actions,
  mutations,
  getters,
  state: INITIAL_STATE,
};
